import {
  FormControl,
  FormControlProps,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  InputLabelProps,
  MenuItem,
  Select,
  SelectProps,
  useThemeProps,
} from '@mui/material';
import type { ComponentsPropsList } from '@mui/material/styles';
import { ClearIcon } from '@mui/x-date-pickers/icons';
import {
  FORM_DROPDOWN_THEME_NAME,
  FORM_FIELD_THEME_NAME,
} from '@onevision-resources/design-theme';
import { CircleX } from 'lucide-react';
import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';

export type FormSelectProps = Omit<FormControlProps, 'required' | 'defaultValue' | 'name'> & {
  label: string;
  options: Option[];
  required?: boolean | string;
  selectProps?: Partial<SelectProps>;
  labelProps?: Partial<InputLabelProps>;
  rules?: Record<string, unknown>;
  clearable?: boolean;
  showErrorText?: boolean;
} & CommonFormFieldProps<'select', string>;

const NO_DROPDOWN_PROPS: ComponentsPropsList['OvFormDropdown'] = {};

export const FormSelect = (inProps: FormSelectProps) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const { selectMenuLook } = useThemeProps({
    props: NO_DROPDOWN_PROPS,
    name: FORM_DROPDOWN_THEME_NAME,
  });
  const {
    name,
    label,
    required,
    selectProps,
    labelProps,
    variant: variantProp,
    size,
    options,
    children,
    rules,
    clearable,
    showErrorText,
    labelAbove = false,
    ...rest
  } = props;
  const variant = variantProp ?? (labelAbove ? 'outlined' : 'standard');
  const generatedId = useId();
  const selectId = selectProps?.id ?? generatedId;
  const labelId = `${generatedId}-label`;
  const { control, formState, getFieldState } = useFormContext();
  const errorMsg = getFieldState(name, formState).error?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  return (
    <FormControl variant={variant} size={size} {...rest} error={hasErrorMsg}>
      <Controller
        name={name}
        control={control}
        rules={{
          ...rules,
          required,
        }}
        render={({ field }) => (
          <>
            {labelAbove ? (
              <FormFieldLabel id={labelId} disabled={rest.disabled || selectProps?.disabled}>
                {label}
              </FormFieldLabel>
            ) : (
              <InputLabel htmlFor={selectId} {...labelProps}>
                {label}
              </InputLabel>
            )}
            <Select
              id={selectId}
              labelId={labelAbove ? labelId : undefined}
              label={labelAbove ? undefined : label}
              variant={variant}
              size={size}
              {...selectProps}
              {...field}
              onChange={(...args) => {
                field.onChange(...args);
                selectProps?.onChange?.(...args);
              }}
              endAdornment={
                clearable && field.value ? (
                  <InputAdornment position="end" sx={selectMenuLook ? undefined : { mr: 2 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        field.onChange('');
                      }}
                    >
                      {selectMenuLook ? <CircleX /> : <ClearIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            >
              {options.map((item) => (
                <MenuItem key={item.value} value={item.value} data-testid={item.testId}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
      />
      {!!showErrorText && !!errorMsg && <FormHelperText>{String(errorMsg)}</FormHelperText>}
    </FormControl>
  );
};
